import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import { compact, differenceBy, flatMap, isEmpty, uniq } from 'lodash';
import ClaimType from 'models/claims/claimType/Model.ClaimType';
import claimTypeDealerGroupAutomationRuleDeleteApi from 'models/dealerManagement/claimTypeDealerGroupAutomationRule/delete/Api.ClaimTypeDealerGroupAutomationRule.Delete';
import claimTypeDealerGroupAutomationRuleSaveApi from 'models/dealerManagement/claimTypeDealerGroupAutomationRule/save/Api.ClaimTypeDealerGroupAutomationRule.Save';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import dealerGroupDeleteApi from 'models/dealerManagement/dealerGroup/delete/Api.DealerGroup.Delete';
import DealerGroup from 'models/dealerManagement/dealerGroup/Model.DealerGroup';
import dealerGroupClaimTypeDeleteApi from 'models/dealerManagement/dealerGroupClaimType/delete/Api.DealerGroupClaimType.Delete';
import dealerGroupClaimTypeSaveApi from 'models/dealerManagement/dealerGroupClaimType/save/Api.DealerGroupClaimType.Save';
import Product from 'models/productManagement/product/Model.Product';
import ProductEnhancement from 'models/productManagement/productEnhancement/Model.ProductEnhancement';
import { useMemo } from 'react';
import { dealerGroupIndexRoute } from './DealerGroup.Index';

export enum DealerGroupAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyDealerGroupAction = Utils.VerifyExtends<
  Module.Claims.DealerGroup.Actions,
  Utils.ValueOf<typeof DealerGroupAction>
>;
export default function useDealerGroup() {
  const { permissions, user, isDealerOrDistributor, isAdministrator } = useAuthStore();
  const pageRouter = usePageRouter<Module.Claims.DealerGroup.Params, Module.Claims.DealerGroup.Query>({
    route: dealerGroupIndexRoute,
  });
  const canView = permissions?.CREATE_DEALERGROUP || permissions?.EDIT_DEALERGROUP || permissions?.DELETE_DEALERGROUP;
  const listApi = useApi(
    {
      action: DealerGroup.list,
      default: { limit: defaultLimit, offset: defaultOffset },
      body: {
        ...pageRouter.query.list,
        distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      },
      callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
      wait: !canView || !user || (!isAdministrator && !isDealerOrDistributor),
    },
    [canView, user, isDealerOrDistributor, isAdministrator]
  );

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const deleteApi = useApi({
    action: dealerGroupDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const dealerIds = useMemo(() => {
    const ids = uniq([...compact(selected?.dealers?.map((d) => d.id))]);
    return ids.length ? ids : undefined;
  }, [selected?.dealers]);
  const { payload: dealersPayload } = useApi(
    {
      action: Dealer.list,
      wait: !dealerIds,
      body: {
        id: dealerIds,
      },
    },
    [dealerIds]
  );

  const [uniqueProductIds, uniqueProductEnhancementIds, uniqueClaimTypeIds] = useMemo(() => {
    const productIds = uniq(flatMap(dealersPayload?.data, (d) => d.products.map((p) => p.id)));
    const productEnhancementIds = uniq(flatMap(dealersPayload?.data, (d) => d.productEnhancements.map((p) => p.id)));
    const claimTypeIds = uniq(flatMap(dealersPayload?.data, (d) => d.dealerClaimTypes.map((p) => p.claimTypeId)));
    return [
      productIds.length ? productIds : undefined,
      productEnhancementIds.length ? productEnhancementIds : undefined,
      claimTypeIds.length ? claimTypeIds : undefined,
    ];
  }, [dealersPayload]);

  const { payload: productsPayload } = useApi(
    {
      action: Product.list,
      wait: !uniqueProductIds,
      body: {
        productId: uniqueProductIds,
      },
    },
    [uniqueProductIds]
  );

  const { payload: productEnhancementsPayload } = useApi(
    {
      action: ProductEnhancement.list,
      wait: !uniqueProductEnhancementIds,
      body: {
        productEnhancementId: uniqueProductEnhancementIds,
      },
    },
    [uniqueProductEnhancementIds]
  );

  const { payload: claimTypesPayload } = useApi(
    {
      action: ClaimType.list,
      wait: !uniqueClaimTypeIds,
      body: {
        id: uniqueClaimTypeIds,
      },
    },
    [uniqueClaimTypeIds]
  );

  const saveDealerGroupAutomationRules = async (
    claimTypeDealerGroupAutomationRules: Utils.FormData<Model.IClaimTypeDealerAutomationRule[]>,
    dealerGroupClaimTypes: Utils.FormData<Model.IDealerClaimType[]>,
    dealerGroupId: number
  ) => {
    for (const claimType of claimTypesPayload?.data ?? []) {
      const removed = differenceBy(
        claimType?.claimTypeAutomationRules ?? [],
        dealerGroupClaimTypes ?? [],
        'claimTypeId'
      );
      for (const removedItem of removed) {
        const dga = selected?.claimTypeDealerGroupAutomationRules?.find(
          (it) => it.claimTypeAutomationRuleId === removedItem?.id
        );
        if (dga && dga.id) {
          await claimTypeDealerGroupAutomationRuleDeleteApi({ id: dga?.id });
        }
      }
    }

    for (const claimTypeDealerGroupAutomationRule of claimTypeDealerGroupAutomationRules || []) {
      const payload = {
        ...claimTypeDealerGroupAutomationRule,
        claimTypeAutomationRuleId: claimTypeDealerGroupAutomationRule.claimTypeAutomationRuleId,
        effects: claimTypeDealerGroupAutomationRule.effects,
        conditions: claimTypeDealerGroupAutomationRule.conditions,
        claimTypeId:
          claimTypeDealerGroupAutomationRule?.claimTypeId ??
          claimTypeDealerGroupAutomationRule?.claimTypeAutomationRule?.claimTypeId,
        dealerGroupId,
      };
      await claimTypeDealerGroupAutomationRuleSaveApi(
        { ...payload },
        {
          claimTypeId: payload.claimTypeId,
        }
      );
    }
  };
  const saveDealerGroupClaimTypes = async (dealerGroupClaimTypes: Utils.FormData<Model.IDealerClaimType[]>) => {
    const removed = differenceBy(selected?.dealerGroupClaimTypes || [], dealerGroupClaimTypes, 'claimTypeId');
    for (const removedItem of removed) {
      if (removedItem.id) {
        await dealerGroupClaimTypeDeleteApi({ id: removedItem.id });
      }
    }
    if (!isEmpty(dealerGroupClaimTypes)) {
      for (const dealerGroupClaimType of dealerGroupClaimTypes) {
        await dealerGroupClaimTypeSaveApi(dealerGroupClaimType);
      }
    }
  };

  return {
    canView,
    pageRouter,
    listApi,
    deleteApi,
    selected,
    productsPayload,
    productEnhancementsPayload,
    claimTypesPayload,
    permissions,
    saveDealerGroupClaimTypes,
    saveDealerGroupAutomationRules,
    views: {
      [DealerGroupAction.DELETE]:
        pageRouter.params.action === DealerGroupAction.DELETE && selected && permissions?.DELETE_DEALERGROUP,
      [DealerGroupAction.EDIT]:
        pageRouter.params.action === DealerGroupAction.EDIT &&
        ((permissions?.CREATE_DEALERGROUP && !selected) || (permissions?.EDIT_DEALERGROUP && selected)),
    },
  };
}

export function useDealerGroupProvider() {
  return useDataProvider<ReturnType<typeof useDealerGroup>>();
}
